import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Check, Lock, ChevronRight, Star, Flame, Sparkles, GraduationCap } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/contexts/AuthContext';
import { formatLevel } from '@/lib/levels';
import api from '@/lib/api';

const TAABIY = 'https://customer-assets.emergentagent.com/job_lamnso-hub/artifacts/ed75ipfo_Ta%C3%A0biy.png';

export default function LearnPage() {
  const { isAuthenticated, user } = useAuth();
  const [units, setUnits] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/lessons/units').then(r => setUnits(r.data.units || [])).catch(() => {}),
      isAuthenticated ? api.get('/users/profile').then(r => setProfile(r.data)).catch(() => {}) : Promise.resolve()
    ]).finally(() => setLoading(false));
  }, [isAuthenticated]);

  const totalCompleted = units.reduce((s, u) => s + (u.completed_lessons || 0), 0);
  const totalLessons = units.reduce((s, u) => s + (u.total_lessons || 0), 0);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-[#E7E5E4] border-t-[#4CAF50] rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="max-w-lg mx-auto px-4 pt-4 pb-20 sm:max-w-2xl">
      {/* Header with stats */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-[#1C1917]" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="learn-title">
            Learn
          </h1>
          {isAuthenticated && profile && (
            <p className="text-[10px] text-[#A8A29E]">{formatLevel(profile.level, 'combined')} &middot; {profile.xp_points} XP</p>
          )}
        </div>
        {isAuthenticated && (
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 text-xs font-semibold text-[#EA580C]">
              <Flame className="w-4 h-4" /> {profile?.streak_count || 0}
            </div>
            <div className="flex items-center gap-1 text-xs font-semibold text-[#4CAF50]">
              <Star className="w-4 h-4" /> {profile?.xp_points || 0}
            </div>
          </div>
        )}
      </div>

      {/* Overall progress */}
      {isAuthenticated && totalLessons > 0 && (
        <div className="bg-white rounded-2xl border border-[#E7E5E4] p-4 mb-5" data-testid="learn-progress">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-[#1C1917]">Course Progress</span>
            <span className="text-[10px] text-[#A8A29E]">{totalCompleted}/{totalLessons} lessons</span>
          </div>
          <Progress value={(totalCompleted / totalLessons) * 100} className="h-2.5 bg-[#F5F5F4]" />
        </div>
      )}

      {/* Daily practice CTA */}
      <Link to="/practice" className="block mb-5" data-testid="learn-practice-cta">
        <div className="bg-gradient-to-r from-[#E8F5E9] to-[#FDF6F0] rounded-2xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/70 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-[#D4A373]" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-[#1C1917]">Daily Reading Practice</p>
            <p className="text-[10px] text-[#57534E]">Bilingual sentences from literature</p>
          </div>
          <ChevronRight className="w-4 h-4 text-[#A8A29E]" />
        </div>
      </Link>

      {/* Unit list */}
      {units.length > 0 ? (
        <div className="space-y-4" data-testid="units-list">
          {units.map((unit) => (
            <UnitBlock key={unit.unit_id} unit={unit} isAuthenticated={isAuthenticated} />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <img src={TAABIY} alt="Taàbiy" className="w-20 h-20 mx-auto mb-3 object-contain animate-bounce-gentle" />
          <p className="text-sm text-[#57534E]">Lessons coming soon!</p>
        </div>
      )}
    </div>
  );
}

function UnitBlock({ unit, isAuthenticated }) {
  const [lessons, setLessons] = useState([]);
  const [expanded, setExpanded] = useState(false);

  const loadLessons = async () => {
    if (lessons.length > 0) { setExpanded(!expanded); return; }
    try {
      const res = await api.get(`/lessons/units/${unit.unit_id}`);
      setLessons(res.data.lessons || []);
      setExpanded(true);
    } catch (err) { console.error(err) }
  };

  const progress = unit.total_lessons > 0 ? Math.round((unit.completed_lessons / unit.total_lessons) * 100) : 0;
  const isComplete = progress === 100;

  return (
    <div className="bg-white rounded-2xl border border-[#E7E5E4] overflow-hidden" data-testid={`unit-${unit.unit_id}`}>
      <button onClick={loadLessons} className="w-full p-4 text-left active:bg-[#FAFAF9] transition-colors" data-testid={`toggle-${unit.unit_id}`}>
        <div className="flex items-center gap-3">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${isComplete ? 'bg-[#EAF1EA]' : 'bg-[#E8F5E9]'}`}>
            {isComplete ? <Check className="w-5 h-5 text-[#588157]" /> : <GraduationCap className="w-5 h-5 text-[#4CAF50]" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-[#1C1917] truncate">{unit.title_en}</p>
            {unit.title_lamnso && <p className="text-[10px] text-[#D4A373] truncate">{unit.title_lamnso}</p>}
            <div className="flex items-center gap-2 mt-1.5">
              <Progress value={progress} className="flex-1 h-1.5 bg-[#F5F5F4]" />
              <span className="text-[10px] font-medium text-[#A8A29E]">{unit.completed_lessons}/{unit.total_lessons}</span>
            </div>
          </div>
          <ChevronRight className={`w-4 h-4 text-[#A8A29E] transition-transform flex-shrink-0 ${expanded ? 'rotate-90' : ''}`} />
        </div>
      </button>

      {expanded && lessons.length > 0 && (
        <div className="border-t border-[#E7E5E4]">
          {lessons.map((lesson, i) => {
            const locked = !lesson.unlocked && isAuthenticated;
            const completed = lesson.completed;
            return (
              <Link key={lesson.lesson_id}
                to={locked ? '#' : `/lessons/${lesson.lesson_id}`}
                onClick={(e) => locked && e.preventDefault()}
                className={`flex items-center gap-3 px-4 py-3 border-b border-[#E7E5E4] last:border-0 ${locked ? 'opacity-40' : 'active:bg-[#FAFAF9]'}`}
                data-testid={`lesson-${lesson.lesson_id}`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                  completed ? 'bg-[#588157] text-white' : locked ? 'bg-[#F5F5F4] text-[#A8A29E]' : 'bg-[#E8F5E9] text-[#4CAF50]'
                }`}>
                  {completed ? <Check className="w-3.5 h-3.5" /> : locked ? <Lock className="w-3 h-3" /> : i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#1C1917] truncate">{lesson.title_en}</p>
                  {lesson.title_lamnso && <p className="text-[10px] text-[#D4A373] truncate">{lesson.title_lamnso}</p>}
                </div>
                {completed && lesson.score > 0 && <span className="text-[10px] font-bold text-[#588157]">{lesson.score}%</span>}
                {!locked && <ChevronRight className="w-3.5 h-3.5 text-[#A8A29E] flex-shrink-0" />}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
